"use client";

import { useState } from "react";
import Link from "next/link";
import {
  Check,
  Copy,
  FileText,
  MessageSquare,
  Sparkles,
  ExternalLink,
} from "lucide-react";
import { useT } from "@/lib/use-i18n";

interface GuideTemplateData {
  title: string;
  description: string;
  code: string;
  prompt: string;
  ctaText?: string;
}

interface GuideTemplateBoxProps {
  template: GuideTemplateData;
}

export function GuideTemplateBox({ template }: GuideTemplateBoxProps) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<"sheet" | "prompt">("sheet");

  const textToCopy = activeTab === "sheet" ? template.code : template.prompt;

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback si clipboard API no está disponible
    }
  }

  const chatHref = `/chat?prompt=${encodeURIComponent(template.prompt)}`;

  // Parse template code into clean visual blocks
  const blocks = template.code
    .trim()
    .split(/\n\s*\n/)
    .map((b) => b.trim())
    .filter(Boolean);

  return (
    <section aria-labelledby="template-box-heading" className="mt-10">
      <div className="lovable-card hardware-surface hardware-surface--static p-5 sm:p-7 rounded-[var(--radius-panel)] border border-border/80 shadow-xs">
        {/* Header with Title and Global Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--accent)] mb-1">
              <Sparkles className="size-3.5" aria-hidden="true" />
              {t("guideTemplateKicker")}
            </span>
            <h2
              id="template-box-heading"
              className="text-heading text-foreground"
            >
              {template.title}
            </h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground transition-all hover:bg-muted hover:border-border/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] active:scale-95 shadow-2xs"
              aria-label={t("guideTemplateCopyAria")}
            >
              {copied ? (
                <>
                  <Check
                    className="size-3.5 text-[var(--success-text)]"
                    aria-hidden="true"
                  />
                  <span>{t("guideTemplateCopied")}</span>
                </>
              ) : (
                <>
                  <Copy
                    className="size-3.5 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <span>
                    {activeTab === "sheet"
                      ? t("guideTemplateCopy")
                      : t("guideTemplateCopyPrompt")}
                  </span>
                </>
              )}
            </button>
            <Link
              href={chatHref}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--accent)] px-3 py-1.5 text-xs font-semibold text-[var(--accent-foreground)] transition-all hover:opacity-95 hover:shadow-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] active:scale-95 shadow-2xs"
            >
              <span>{template.ctaText ?? t("guideTemplateFormat")}</span>
              <ExternalLink className="size-3" aria-hidden="true" />
            </Link>
          </div>
        </div>

        <p className="text-body text-foreground-soft mb-5 text-pretty text-sm">
          {template.description}
        </p>

        {/* View Switcher Tabs */}
        <div
          role="tablist"
          aria-label={t("guideTemplateTablistAria")}
          className="flex items-center gap-2 p-1 rounded-lg bg-muted/40 border border-border/60 mb-5 w-fit max-w-full overflow-x-auto"
        >
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "sheet"}
            onClick={() => setActiveTab("sheet")}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] ${
              activeTab === "sheet"
                ? "bg-card text-foreground shadow-2xs border border-border/80"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/30"
            }`}
          >
            <FileText className="size-3.5" aria-hidden="true" />
            <span>{t("guideTemplateSheetTab")}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "prompt"}
            onClick={() => setActiveTab("prompt")}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] ${
              activeTab === "prompt"
                ? "bg-card text-foreground shadow-2xs border border-border/80"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/30"
            }`}
          >
            <MessageSquare className="size-3.5" aria-hidden="true" />
            <span>{t("guideTemplatePromptTab")}</span>
          </button>
        </div>

        {/* Tab 1: Realistic Document Sheet Preview */}
        {activeTab === "sheet" && (
          <div className="rounded-xl border border-border/80 bg-background/95 p-4 sm:p-7 shadow-sm">
            {/* Top Sheet Info bar */}
            <div className="flex items-center justify-between pb-3.5 mb-5 border-b border-border/60 text-xs">
              <div className="flex items-center gap-2 text-foreground font-semibold">
                <span
                  className="size-2 rounded-full bg-[var(--accent)] animate-pulse"
                  aria-hidden="true"
                />
                <span>{t("guideTemplateOfficialCover")}</span>
              </div>
              <span className="font-mono text-[11px] text-muted-foreground uppercase tracking-wider">
                {t("guideTemplatePaperLetterCentered")}
              </span>
            </div>

            {/* Simulated Paper Sheet */}
            <div className="mx-auto max-w-lg rounded-lg border border-border/70 bg-card/60 p-5 sm:p-9 shadow-inner relative">
              {/* Top Margin Annotation */}
              <div className="text-[11px] font-mono text-muted-foreground flex justify-between pb-3 border-b border-dashed border-border/40 mb-6">
                <span>↑ 3,0 cm sup.</span>
                <span className="text-[var(--accent)] font-semibold">
                  ← 4,0 cm margen de encuadernación
                </span>
                <span>2,0 cm der. →</span>
              </div>

              {/* Centered Document Content */}
              <div className="text-center space-y-6 sm:space-y-8 text-foreground text-xs sm:text-sm">
                {blocks.map((block, idx) => {
                  const lines = block.split("\n").map((l) => l.trim());
                  if (idx === 0) {
                    return (
                      <div key={`block-header-${idx}`} className="space-y-1.5">
                        <p className="font-bold tracking-wide uppercase text-sm sm:text-base leading-snug text-foreground">
                          {lines[0]}
                        </p>
                        {lines.slice(1).map((sub, sIdx) => (
                          <p
                            key={sIdx}
                            className="text-foreground-soft text-xs sm:text-sm font-medium"
                          >
                            {sub}
                          </p>
                        ))}
                      </div>
                    );
                  }

                  if (idx === blocks.length - 1) {
                    return (
                      <div
                        key={`block-footer-${idx}`}
                        className="space-y-1 text-xs uppercase font-medium pt-4 border-t border-dashed border-border/40"
                      >
                        {lines.map((instLine, iIdx) => (
                          <p
                            key={iIdx}
                            className={
                              iIdx === 0
                                ? "font-bold text-foreground"
                                : iIdx === lines.length - 1
                                  ? "font-mono text-foreground font-bold pt-1"
                                  : "text-foreground-soft"
                            }
                          >
                            {instLine}
                          </p>
                        ))}
                      </div>
                    );
                  }

                  return (
                    <div
                      key={`block-body-${idx}`}
                      className="space-y-1 text-xs sm:text-sm"
                    >
                      {lines.map((l, lineIdx) => (
                        <p
                          key={lineIdx}
                          className={
                            idx === 1
                              ? "font-semibold uppercase tracking-wider text-foreground"
                              : "text-foreground-soft"
                          }
                        >
                          {l}
                        </p>
                      ))}
                    </div>
                  );
                })}
              </div>

              {/* Bottom Margin Annotation */}
              <div className="text-[11px] font-mono text-muted-foreground text-center pt-5 mt-6 border-t border-dashed border-border/40">
                <span>
                  ↓ 3,0 cm inf. • Paginación centrada a 2 cm (sin número visible
                  en portada)
                </span>
              </div>
            </div>

            {/* Practical Helper Callout */}
            <div className="mt-4 pt-3 flex items-start gap-2 text-xs text-muted-foreground border-t border-border/50">
              <span className="text-[var(--accent)] font-bold">
                {t("guideTemplateTipLabel")}
              </span>
              <p>{t("guideTemplateTip")}</p>
            </div>
          </div>
        )}

        {/* Tab 2: Prompt for Vortixy Chat */}
        {activeTab === "prompt" && (
          <div className="rounded-xl border border-border/80 bg-background/95 p-4 sm:p-6 shadow-sm space-y-4">
            <div className="flex items-center justify-between text-xs pb-3 border-b border-border/60">
              <span className="font-semibold text-foreground">
                {t("guideTemplateReviewPromptTitle")}
              </span>
              <span className="font-mono text-[11px] text-muted-foreground">
                {t("guideTemplateAutoReviewNtc")}
              </span>
            </div>
            <div className="p-4 rounded-lg bg-card/60 border border-border/70 font-mono text-xs text-foreground leading-relaxed whitespace-pre-wrap">
              {template.prompt}
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <Link
                href={chatHref}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--accent)] px-3 py-1.5 text-xs font-semibold text-[var(--accent-foreground)] transition-all hover:opacity-95"
              >
                <span>{t("guideTemplateSendPromptToChat")}</span>
                <ExternalLink className="size-3" aria-hidden="true" />
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
